import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, BarChart3, Calendar, RefreshCw, Plus } from "lucide-react";
import { toast } from "sonner";
import Gantt from "frappe-gantt";
import "@/styles/frappe-gantt.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  "Pending": "bg-slate-100 text-slate-700",
  "In Progress": "bg-blue-100 text-blue-700",
  "Completed": "bg-emerald-100 text-emerald-700",
  "Delayed": "bg-red-100 text-red-700",
  "On Hold": "bg-amber-100 text-amber-700"
};

export default function GanttPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const ganttRef = useRef(null);
  const ganttInstance = useRef(null);

  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("Week");

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectRes, milestonesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects/${projectId}/milestones`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProject(projectRes.data);
      setMilestones(milestonesRes.data || []);
    } catch (error) {
      toast.error("Failed to load project data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ganttRef.current || milestones.length === 0 || !project) return;

    // Clear previous instance
    ganttRef.current.innerHTML = '';

    // Convert milestones to Gantt tasks
    const tasks = milestones.map((ms, idx) => {
      const startDate = ms.planned_date || project.start_date;
      // Estimate end date as 7 days after start if not specified
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);
      
      return {
        id: ms.id,
        name: ms.name,
        start: startDate,
        end: endDate.toISOString().split('T')[0],
        progress: ms.completion_percent || 0,
        dependencies: '',
        custom_class: ms.status === 'Completed' ? 'bar-completed' : 
                     ms.status === 'In Progress' ? 'bar-in-progress' :
                     ms.status === 'Delayed' ? 'bar-delayed' : ''
      };
    });

    if (tasks.length > 0) {
      try {
        ganttInstance.current = new Gantt(ganttRef.current, tasks, {
          view_mode: viewMode,
          date_format: 'YYYY-MM-DD',
          language: 'en',
          bar_height: 30,
          bar_corner_radius: 5,
          padding: 20,
          custom_popup_html: (task) => {
            const milestone = milestones.find(m => m.id === task.id);
            return `
              <div class="gantt-popup-title">${task.name}</div>
              <div class="gantt-popup-subtitle">
                <p><strong>Status:</strong> ${milestone?.status || 'Pending'}</p>
                <p><strong>Progress:</strong> ${task.progress}%</p>
                <p><strong>Planned Date:</strong> ${task.start}</p>
                ${milestone?.description ? `<p><strong>Description:</strong> ${milestone.description}</p>` : ''}
              </div>
            `;
          }
        });
      } catch (error) {
        console.error("Failed to initialize Gantt chart:", error);
      }
    }

    return () => {
      if (ganttRef.current) {
        ganttRef.current.innerHTML = '';
      }
    };
  }, [milestones, project, viewMode]);

  const changeViewMode = (mode) => {
    setViewMode(mode);
    if (ganttInstance.current) {
      try {
        ganttInstance.current.change_view_mode(mode);
      } catch (error) {
        // Re-render will handle the view change
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brillien-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="gantt-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(`/projects/${projectId}`)}
              className="h-10 w-10 p-0"
              data-testid="back-to-project"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-brillien-600" />
                Project Timeline - Gantt Chart
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {project?.name} | {project?.client}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Mode Selector */}
            <Select value={viewMode} onValueChange={changeViewMode}>
              <SelectTrigger className="w-32" data-testid="gantt-view-mode">
                <SelectValue placeholder="View" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Day">Day</SelectItem>
                <SelectItem value="Week">Week</SelectItem>
                <SelectItem value="Month">Month</SelectItem>
                <SelectItem value="Year">Year</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={fetchData} data-testid="refresh-gantt">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            
            <Button 
              onClick={() => navigate(`/projects/${projectId}?tab=timeline`)}
              className="bg-brillien-600 hover:bg-brillien-700"
              data-testid="manage-milestones"
            >
              <Plus className="h-4 w-4 mr-2" />
              Manage Milestones
            </Button>
          </div>
        </div>
      </div>

      {/* Project Info Bar */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-3">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500 dark:text-slate-400">Start:</span>
            <span className="font-medium text-slate-700 dark:text-slate-300">{project?.start_date}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500 dark:text-slate-400">Target:</span>
            <span className="font-medium text-slate-700 dark:text-slate-300">{project?.target_date}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 dark:text-slate-400">Milestones:</span>
            <Badge className="bg-brillien-100 text-brillien-700">{milestones.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 dark:text-slate-400">Completed:</span>
            <Badge className="bg-emerald-100 text-emerald-700">
              {milestones.filter(m => m.status === 'Completed').length}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 dark:text-slate-400">In Progress:</span>
            <Badge className="bg-blue-100 text-blue-700">
              {milestones.filter(m => m.status === 'In Progress').length}
            </Badge>
          </div>
        </div>
      </div>

      {/* Gantt Chart Area */}
      <div className="p-6">
        {milestones.length === 0 ? (
          <Card className="p-12 text-center">
            <BarChart3 className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Milestones Yet</h3>
            <p className="text-slate-500 mb-4">Add milestones to see the Gantt chart visualization</p>
            <Button 
              onClick={() => navigate(`/projects/${projectId}?tab=timeline`)}
              className="bg-brillien-600 hover:bg-brillien-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Milestones
            </Button>
          </Card>
        ) : (
          <Card className="p-4 overflow-hidden">
            <div className="gantt-full-page-wrapper">
              <style>{`
                .gantt-full-page-wrapper {
                  overflow-x: auto;
                  overflow-y: auto;
                  background: #ffffff;
                  border-radius: 8px;
                  min-height: calc(100vh - 280px);
                }
                .gantt-full-page-wrapper svg.gantt {
                  background: #ffffff !important;
                  min-height: calc(100vh - 300px);
                }
                .gantt-full-page-wrapper .gantt .bar-wrapper .bar {
                  fill: #3b82f6 !important;
                  stroke: none !important;
                  rx: 5;
                  ry: 5;
                }
                .gantt-full-page-wrapper .gantt .bar-wrapper .bar-progress {
                  fill: #1d4ed8 !important;
                }
                .gantt-full-page-wrapper .gantt .bar-wrapper .bar-label {
                  fill: #1e293b !important;
                  font-weight: 600 !important;
                  font-size: 13px !important;
                }
                .gantt-full-page-wrapper .gantt .bar-wrapper.bar-completed .bar {
                  fill: #10b981 !important;
                }
                .gantt-full-page-wrapper .gantt .bar-wrapper.bar-completed .bar-progress {
                  fill: #059669 !important;
                }
                .gantt-full-page-wrapper .gantt .bar-wrapper.bar-in-progress .bar {
                  fill: #3b82f6 !important;
                }
                .gantt-full-page-wrapper .gantt .bar-wrapper.bar-delayed .bar {
                  fill: #ef4444 !important;
                }
                .gantt-full-page-wrapper .gantt .bar-wrapper.bar-delayed .bar-progress {
                  fill: #dc2626 !important;
                }
                .gantt-full-page-wrapper .gantt .grid-header {
                  fill: #f1f5f9 !important;
                }
                .gantt-full-page-wrapper .gantt .grid-row {
                  fill: #ffffff !important;
                }
                .gantt-full-page-wrapper .gantt .grid-row:nth-child(even) {
                  fill: #f8fafc !important;
                }
                .gantt-full-page-wrapper .gantt .tick {
                  stroke: #e2e8f0 !important;
                }
                .gantt-full-page-wrapper .gantt text {
                  fill: #334155 !important;
                }
                .gantt-full-page-wrapper .gantt .lower-text, 
                .gantt-full-page-wrapper .gantt .upper-text {
                  fill: #64748b !important;
                  font-size: 12px !important;
                }
                .gantt-full-page-wrapper .gantt .today-highlight {
                  fill: #fef3c7 !important;
                }
                .gantt-full-page-wrapper .gantt .handle {
                  fill: #0ea5e9 !important;
                }
                .gantt-popup-title {
                  font-weight: 600;
                  font-size: 16px;
                  margin-bottom: 10px;
                  color: #1e293b;
                }
                .gantt-popup-subtitle {
                  font-size: 13px;
                  color: #64748b;
                }
                .gantt-popup-subtitle p {
                  margin: 4px 0;
                }
                .gantt-popup-subtitle strong {
                  color: #334155;
                }
              `}</style>
              <div ref={ganttRef}></div>
            </div>
          </Card>
        )}
      </div>

      {/* Legend */}
      {milestones.length > 0 && (
        <div className="px-6 pb-6">
          <Card className="p-4">
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Legend</h4>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-500"></div>
                <span className="text-sm text-slate-600 dark:text-slate-400">Pending</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-600"></div>
                <span className="text-sm text-slate-600 dark:text-slate-400">In Progress</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-emerald-500"></div>
                <span className="text-sm text-slate-600 dark:text-slate-400">Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span className="text-sm text-slate-600 dark:text-slate-400">Delayed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-200"></div>
                <span className="text-sm text-slate-600 dark:text-slate-400">Today</span>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
